// src/cmps/review/ReviewPopup.jsx
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { images } from '../../services/images.service'
import toast from 'react-hot-toast'
import { useDispatch } from 'react-redux'
import { addReview, editReview } from '../../store/slices/review.slice'
import { Button } from '../ui/elements'

export function ReviewPopup({ initialData, animeId, loggedInUser, onClose }) {
  const dispatch = useDispatch()

  // recommended: true = הוספה לרשימת המלצות, false = הוספה לרשימת אכזבות, null = לא בחר
  const [recommended, setRecommended] = useState(
    initialData?.recommended ?? null
  )
  const [rating, setRating] = useState(initialData?.rating ?? null)
  const [content, setContent] = useState(initialData?.content ?? '')

  // מפה לתיאור הציון
  const ratingMeanings = {
    1: 'גרוע מאוד',
    2: 'גרוע',
    3: 'לא טוב',
    4: 'מתחת למצוין',
    5: 'סביר',
    6: 'בסדר',
    7: 'טוב',
    8: 'טוב מאוד',
    9: 'מעולה',
    10: 'מצוין',
  }

  // סנכרון עם הנתונים הראשוניים
  useEffect(() => {
    setRecommended(initialData?.recommended ?? null)
    setRating(initialData?.rating ?? null)
    setContent(initialData?.content ?? '')
  }, [initialData])

  // שליחת הביקורת
  async function handleSubmit(e) {
    e.preventDefault()
    if (rating === null) {
      toast.error('חובה לבחור האם ממליץ ולספק ציון')
      return
    }

    const reviewPayload = {
      recommended,
      rating,
      content,
      animeId: Number(animeId),
    }

    try {
      if (initialData?._id) {
        // מצב עריכה
        await dispatch(
          editReview({
            reviewId: initialData._id,
            updatedData: reviewPayload,
            loggedInUser,
          })
        ).unwrap()
        toast.success('הביקורת נערכה בהצלחה!')
      } else {
        // מצב הוספה
        const newReview = {
          ...reviewPayload,
          userId: loggedInUser._id,
          likes: 0,
          likedBy: [],
          reports: [],
          hidden: false,
        }
        await dispatch(
          addReview({
            reviewData: newReview,
            loggedInUser,
          })
        ).unwrap()
        toast.success('ביקורת נוספה בהצלחה!')
      }
      onClose?.()
    } catch (err) {
      console.error('Error submitting review:', err)
      toast.error('שגיאה בעת שמירת הביקורת')
    }
  }

  return (
    <div
      className="bg-secondaryBackground relative w-[400px] p-4"
      style={{
        borderImage: `url(${images.redBorder}) 10 stretch`,
        borderWidth: '2px',
        borderStyle: 'solid',
      }}
    >
      {/* כפתור סגירה */}
      <button
        className="absolute top-4 right-4"
        onClick={onClose ?? (() => navigate(-1))}
      >
        <img src={images.closeMenu} alt="Close" className="w-10 h-10" />
      </button>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col items-center gap-4"
      >
        {/* כותרת */}
        <h3 className="text-h3">
          {initialData?._id ? 'ערוך ביקורת' : 'דרג את האנימה'}
        </h3>

        {/* הדירוג שלך + משמעות */}
        <div className="flex justify-between w-[90%]">
          <h6 className="text-softText text-h6">הדירוג שלך:</h6>
          <h6 className="text-muted text-h6">
            {rating ? ratingMeanings[rating] : ''}
          </h6>
        </div>

        {/* כפתורי ציון 1–10 */}
        <div className="flex gap-2 w-[90%]">
          {Array.from({ length: 10 }, (_, i) => 10 - i).map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => setRating(num)}
              className={`w-7 h-7 flex items-center justify-center ${
                rating === num
                  ? 'bg-primary text-white'
                  : 'hover:bg-primary hover:text-white transition-colors bg-white text-black '
              }`}
            >
              <span className="text-sm leading-tight tracking-wide">{num}</span>
            </button>
          ))}
        </div>

        {/* תיבת ביקורת */}
        <div className="w-[90%] h-[200px] p-2 bg-transparent border border-[rgba(255,255,255,0.15)]">
          <textarea
            className="bg-transparent placeholder-softText text-white w-full h-full resize-none overflow-auto p-2"
            placeholder="כתוב ביקורת (אופציונלי)"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>

        {/* מיכל קבוע לגובה כדי שלא יהיה קפיצה */}
        <div className="w-[90%] flex items-center gap-4">
          {/* המלצות */}
          <label
            className={`flex items-center gap-2 whitespace-nowrap text-sm ${
              rating !== null && rating >= 6
                ? 'text-white'
                : 'text-muted line-through'
            }`}
          >
            <input
              type="checkbox"
              disabled={rating === null || rating < 6}
              checked={recommended === true}
              onChange={(e) => setRecommended(e.target.checked ? true : null)}
              className="w-5 h-5"
            />
            הוסף לרשימת ההמלצות
          </label>

          {/* אכזבות */}
          <label
            className={`flex items-center gap-2 whitespace-nowrap text-sm ${
              rating !== null && rating <= 5
                ? 'text-white'
                : 'text-muted line-through'
            }`}
          >
            <input
              type="checkbox"
              disabled={rating === null || rating > 5}
              checked={recommended === false}
              onChange={(e) => setRecommended(e.target.checked ? false : null)}
              className="w-5 h-5"
            />
            הוסף לרשימת האכזבות
          </label>
        </div>

        {/* כפתור שליחה */}
        <Button
          text="שליחת ביקורת"
          width={300}
          height={59}
          fontSize="text-md"
          textColor="text-white"
          bgColor="fill-[#BD0029]"
          hoverBgColor="hover:fill-[#a80020]"
          onClick={handleSubmit}
          type="submit"
          aria-label="שלח ביקורת על האנימה"
          title="שליחת ביקורת"
        />
      </form>
    </div>
  )
}

ReviewPopup.propTypes = {
  initialData: PropTypes.shape({
    _id: PropTypes.string,
    recommended: PropTypes.bool,
    rating: PropTypes.number,
    content: PropTypes.string,
  }),
  animeId: PropTypes.number.isRequired,
  loggedInUser: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
}
